/**
 * @file ViewModeToggle.js
 * @description Grid/List view toggle buttons, theme-connected via the client's accent color CSS variables. Reuses the exact grid/list icons from the Asset Library (src/components/assets/AssetLibrary.js).
 */

import styles from './ViewModeToggle.module.scss';
import { BenefitsViewModeEnum } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

function GridIcon({ isActive }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="19"
            fill={
                isActive
                    ? 'var(--iNGAGED-AccessoryForeground-Color1)'
                    : 'var(--iNGAGED-Neutral-Color)'
            }
            viewBox="0 0 16 16"
        >
            <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5z" />
        </svg>
    );
}

function ListIcon({ isActive }) {
    const lineColor = isActive
        ? 'var(--iNGAGED-AccessoryForeground-Color1)'
        : 'var(--iNGAGED-Neutral-Color)';
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
        >
            <circle
                cx="16"
                cy="16"
                r="15.5"
                fill={isActive ? '1366FF' : 'white'}
            />
            <line x1="9.6" y1="15.5" x2="22.43" y2="15.5" stroke={lineColor} />
            <line x1="9.6" y1="20.3" x2="22.43" y2="20.3" stroke={lineColor} />
            <line x1="9.6" y1="10.7" x2="22.43" y2="10.7" stroke={lineColor} />
        </svg>
    );
}

function ToggleButton({
    mode,
    label,
    isActive,
    iconPadding,
    onSelect,
    children,
}) {
    return (
        <div
            title={label}
            onClick={() => onSelect(mode)}
            className={styles.toggleButton}
        >
            <div
                className={
                    isActive ? styles.activeViewMode : styles.inactiveViewMode
                }
                style={{ padding: iconPadding }}
            >
                {children}
            </div>
            <span className={styles.toggleLabel}>{label}</span>
        </div>
    );
}

export default function ViewModeToggle({ selectedViewMode, onChange }) {
    return (
        <div className={styles.container}>
            <ToggleButton
                mode={BenefitsViewModeEnum.Grid}
                label={getText('msg_602bf12902a7')}
                iconPadding="6px"
                isActive={selectedViewMode === BenefitsViewModeEnum.Grid}
                onSelect={onChange}
            >
                <GridIcon
                    isActive={selectedViewMode === BenefitsViewModeEnum.Grid}
                />
            </ToggleButton>
            <ToggleButton
                mode={BenefitsViewModeEnum.List}
                label={getText('msg_fb1bde1b6c26')}
                iconPadding="0px"
                isActive={selectedViewMode === BenefitsViewModeEnum.List}
                onSelect={onChange}
            >
                <ListIcon
                    isActive={selectedViewMode === BenefitsViewModeEnum.List}
                />
            </ToggleButton>
        </div>
    );
}
